import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Section blocks: { p }, { ul: [text] }, { rules: [before, linkText, after] }, { contact: true }
const CONTENT = {
  en: {
    back: 'Back',
    title: 'Terms of Service',
    updated: 'Last updated: January 22, 2026',
    footer: '© 2026 SocraQuest. All rights reserved.',
    contact: { email: 'Email', website: 'Website' },
    sections: [
      ['1. Agreement to Terms', [
        { p: 'By accessing or using SocraQuest ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of the terms, you may not access the Service. These Terms apply to all visitors, users, and others who access or use the Service.' },
      ]],
      ['2. Description of Service', [
        { p: 'SocraQuest is a daily learning and assessment platform that provides users with educational content through interactive assessments, personalized analytics, and learning paths. The Service includes features such as daily challenges, image-based assessments, progress insights, leaderboards, and a rewards program.' },
      ]],
      ['3. User Accounts', [
        { ul: [
          'You must provide accurate and complete information when creating an account',
          'You are responsible for maintaining the security of your account credentials',
          'You must notify us immediately of any unauthorized access to your account',
          "You may not use another person's account without permission",
          'You must be at least 13 years old to create an account',
        ] },
      ]],
      ['4. Acceptable Use', [
        { p: 'You agree not to:' },
        { ul: [
          'Use the Service for any illegal purpose',
          'Attempt to cheat, hack, or manipulate assessment results',
          'Use automated scripts or bots to interact with the Service',
          'Harass, abuse, or harm other users',
          'Impersonate any person or entity',
          'Interfere with or disrupt the Service or servers',
          'Attempt to gain unauthorized access to any part of the Service',
          'Share your account credentials with others',
        ] },
      ]],
      ['5. Rewards Program', [
        { p: 'SocraQuest may offer a rewards program with the following conditions:' },
        { ul: [
          'Rewards are subject to eligibility requirements as specified in the app',
          'We reserve the right to modify or terminate the rewards program at any time',
          'Rewards may only be claimed by users who meet all eligibility criteria',
          'Any attempt to fraudulently claim rewards will result in account termination',
          'Tax obligations related to rewards are the responsibility of the user',
          'Minimum payout thresholds may apply',
        ] },
        { rules: ['The full eligibility requirements, prizes and payout process are set out in the ', 'Official Rules', '. Apple Inc. and Google LLC are not sponsors of, and are not involved in, the rewards program in any manner.'] },
      ]],
      ['6. Intellectual Property', [
        { p: 'The Service and its original content, features, and functionality are owned by SocraQuest and are protected by international copyright, trademark, and other intellectual property laws.' },
        { p: 'Assessment content, including questions, answers, and AI-generated images, are the property of SocraQuest. You may not reproduce, distribute, or create derivative works without our express written permission.' },
      ]],
      ['7. User Content', [
        { p: 'By submitting content to the Service (such as usernames or profile information), you grant us a non-exclusive, worldwide, royalty-free license to use, display, and distribute such content in connection with the Service.' },
      ]],
      ['8. Disclaimers', [
        { p: 'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.' },
        { ul: [
          'We do not guarantee the accuracy of assessment content',
          'We do not guarantee uninterrupted or error-free service',
          'We are not responsible for any loss of data or progress',
          'Educational content is for entertainment purposes and should not be relied upon as professional advice',
        ] },
      ]],
      ['9. Limitation of Liability', [
        { p: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, SOCRAQUEST SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE SERVICE.' },
      ]],
      ['10. Termination', [
        { p: 'We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the Service will immediately cease. All provisions of these Terms which should survive termination shall survive.' },
      ]],
      ['11. Changes to Terms', [
        { p: 'We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the new Terms on the Service and updating the "Last updated" date. Your continued use of the Service after any changes constitutes acceptance of the new Terms.' },
      ]],
      ['12. Governing Law', [
        { p: 'These Terms shall be governed by and construed in accordance with the laws of the Slovak Republic, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of the Slovak Republic.' },
      ]],
      ['13. Contact Us', [
        { p: 'If you have any questions about these Terms, please contact us:' },
        { contact: true },
      ]],
    ],
  },
  sk: {
    back: 'Späť',
    title: 'Podmienky používania',
    updated: 'Posledná aktualizácia: 22. januára 2026',
    footer: '© 2026 SocraQuest. Všetky práva vyhradené.',
    contact: { email: 'E-mail', website: 'Webová stránka' },
    sections: [
      ['1. Súhlas s podmienkami', [
        { p: 'Prístupom k SocraQuest alebo jeho používaním („Služba“) súhlasíte s tým, že ste viazaní týmito Podmienkami používania („Podmienky“). Ak nesúhlasíte s ktoroukoľvek ich časťou, Službu nesmiete používať. Tieto Podmienky platia pre všetkých návštevníkov, používateľov a ďalšie osoby, ktoré k Službe pristupujú alebo ju používajú.' },
      ]],
      ['2. Popis Služby', [
        { p: 'SocraQuest je platforma na každodenné učenie a hodnotenie vedomostí, ktorá poskytuje vzdelávací obsah prostredníctvom interaktívnych hodnotení, osobných analýz a vzdelávacích ciest. Služba zahŕňa napríklad denné výzvy, hodnotenia s obrázkami, prehľad pokroku, rebríčky a program odmien.' },
      ]],
      ['3. Používateľské účty', [
        { ul: [
          'Pri vytváraní účtu musíte uviesť pravdivé a úplné údaje',
          'Zodpovedáte za bezpečnosť svojich prihlasovacích údajov',
          'Akýkoľvek neoprávnený prístup k Vášmu účtu nám musíte bezodkladne oznámiť',
          'Účet inej osoby nesmiete používať bez jej súhlasu',
          'Na vytvorenie účtu musíte mať aspoň 13 rokov',
        ] },
      ]],
      ['4. Prijateľné používanie', [
        { p: 'Zaväzujete sa, že nebudete:' },
        { ul: [
          'používať Službu na nezákonné účely,',
          'sa pokúšať podvádzať, hackovať alebo manipulovať výsledky hodnotení,',
          'používať automatizované skripty alebo botov na prácu so Službou,',
          'obťažovať, urážať alebo poškodzovať iných používateľov,',
          'sa vydávať za inú osobu alebo subjekt,',
          'narúšať alebo rušiť Službu či servery,',
          'sa pokúšať získať neoprávnený prístup k akejkoľvek časti Služby,',
          'zdieľať svoje prihlasovacie údaje s inými osobami.',
        ] },
      ]],
      ['5. Program odmien', [
        { p: 'SocraQuest môže ponúkať program odmien za týchto podmienok:' },
        { ul: [
          'Na odmeny sa vzťahujú podmienky uvedené v aplikácii',
          'Program odmien môžeme kedykoľvek zmeniť alebo ukončiť',
          'Odmenu môžu získať len používatelia, ktorí splnia všetky podmienky',
          'Akýkoľvek pokus o podvodné získanie odmeny vedie k zrušeniu účtu',
          'Daňové povinnosti súvisiace s odmenami nesie používateľ',
          'Môže platiť minimálna suma na výplatu',
        ] },
        { rules: ['Všetky podmienky, výhry a postup vyplácania sú uvedené v dokumente ', 'Oficiálne pravidlá', '. Spoločnosti Apple Inc. a Google LLC nie sú sponzormi programu odmien a nijako sa na ňom nepodieľajú.'] },
      ]],
      ['6. Duševné vlastníctvo', [
        { p: 'Služba a jej pôvodný obsah, funkcie a funkcionalita sú vlastníctvom SocraQuest a sú chránené medzinárodnými predpismi o autorských právach, ochranných známkach a inom duševnom vlastníctve.' },
        { p: 'Obsah hodnotení vrátane otázok, odpovedí a obrázkov vytvorených umelou inteligenciou je vlastníctvom SocraQuest. Bez nášho výslovného písomného súhlasu ho nesmiete rozmnožovať, šíriť ani z neho vytvárať odvodené diela.' },
      ]],
      ['7. Obsah používateľov', [
        { p: 'Odoslaním obsahu do Služby (napríklad používateľského mena alebo údajov profilu) nám udeľujete nevýhradnú, celosvetovú a bezodplatnú licenciu na používanie, zobrazovanie a šírenie tohto obsahu v súvislosti so Službou.' },
      ]],
      ['8. Vylúčenie záruk', [
        { p: 'SLUŽBA SA POSKYTUJE „TAK, AKO JE“ A „TAK, AKO JE DOSTUPNÁ“, BEZ AKÝCHKOĽVEK VÝSLOVNÝCH ALEBO PREDPOKLADANÝCH ZÁRUK.' },
        { ul: [
          'Nezaručujeme správnosť obsahu hodnotení',
          'Nezaručujeme nepretržitú ani bezchybnú prevádzku',
          'Nezodpovedáme za stratu údajov ani pokroku',
          'Vzdelávací obsah slúži na zábavu a nenahrádza odborné poradenstvo',
        ] },
      ]],
      ['9. Obmedzenie zodpovednosti', [
        { p: 'V NAJVÄČŠOM ROZSAHU, KTORÝ PRIPÚŠŤA ZÁKON, SOCRAQUEST NEZODPOVEDÁ ZA ŽIADNE NEPRIAME, NÁHODNÉ, OSOBITNÉ, NÁSLEDNÉ ANI SANKČNÉ ŠKODY, ANI ZA STRATU ZISKU ALEBO PRÍJMOV, ČI UŽ VZNIKLI PRIAMO ALEBO NEPRIAMO, ANI ZA STRATU ÚDAJOV, MOŽNOSTI POUŽÍVANIA, DOBRÉHO MENA ALEBO INÉ NEHMOTNÉ STRATY VYPLÝVAJÚCE Z POUŽÍVANIA SLUŽBY.' },
      ]],
      ['10. Ukončenie', [
        { p: 'Váš účet a prístup k Službe môžeme okamžite zrušiť alebo pozastaviť, bez predchádzajúceho upozornenia a bez zodpovednosti, z akéhokoľvek dôvodu vrátane porušenia týchto Podmienok. Po ukončení Vaše právo používať Službu okamžite zaniká. Ustanovenia týchto Podmienok, ktoré majú svojou povahou trvať aj po ukončení, zostávajú v platnosti.' },
      ]],
      ['11. Zmeny Podmienok', [
        { p: 'Tieto Podmienky môžeme kedykoľvek zmeniť. O podstatných zmenách Vás budeme informovať zverejnením nových Podmienok v Službe a aktualizáciou dátumu „Posledná aktualizácia“. Ďalším používaním Služby po zmene vyjadrujete súhlas s novými Podmienkami.' },
      ]],
      ['12. Rozhodné právo', [
        { p: 'Tieto Podmienky sa riadia právnym poriadkom Slovenskej republiky bez ohľadu na jeho kolízne normy. Na riešenie sporov vyplývajúcich z týchto Podmienok sú výlučne príslušné súdy Slovenskej republiky.' },
      ]],
      ['13. Kontakt', [
        { p: 'Ak máte otázky k týmto Podmienkam, kontaktujte nás:' },
        { contact: true },
      ]],
    ],
  },
};

function Block({ block, c }) {
  if (block.p) return <p className="text-slate-600 mb-4">{block.p}</p>;
  if (block.ul) {
    return (
      <ul className="list-disc list-inside text-slate-600 mb-4 space-y-2">
        {block.ul.map((item) => <li key={item}>{item}</li>)}
      </ul>
    );
  }
  if (block.rules) {
    return (
      <p className="text-slate-600 mt-4">
        {block.rules[0]}
        <Link to="/rewards-rules" className="text-teal-600 hover:text-teal-700 underline">{block.rules[1]}</Link>
        {block.rules[2]}
      </p>
    );
  }
  if (block.contact) {
    return (
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <p className="text-slate-700"><strong>SocraQuest</strong></p>
        <p className="text-slate-600">{c.contact.email}: support@socraquest.sk</p>
        <p className="text-slate-600">{c.contact.website}: https://socraquest.sk</p>
      </div>
    );
  }
  return null;
}

export default function TermsOfServicePage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').toLowerCase().startsWith('sk') ? 'sk' : 'en';
  const c = CONTENT[lang];

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <button onClick={goBack} className="flex items-center text-teal-600 hover:text-teal-700 mr-4" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-1" />
            {c.back}
          </button>
          <h1 className="text-xl font-bold text-slate-800">SocraQuest</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8" data-testid="terms-of-service">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">{c.title}</h1>
          <p className="text-slate-500 mb-8">{c.updated}</p>

          <div className="prose prose-slate max-w-none">
            {c.sections.map(([heading, blocks]) => (
              <section key={heading} className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-4">{heading}</h2>
                {blocks.map((block, i) => <Block key={i} block={block} c={c} />)}
              </section>
            ))}
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-slate-200 mt-8">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-slate-500 text-sm">
          {c.footer}
        </div>
      </footer>
    </div>
  );
}
